import * as React from 'react';

const isTestEnv = process.env.NODE_ENV === 'test';

export function getParentNode(element: HTMLElement) {
  if (element.nodeName === 'HTML') {
    return element;
  }
  // @ts-ignore
  return element.parentNode || element.host;
}

function getStyleComputedProperty(element: HTMLElement) {
  // NOTE: 1 DOM access here
  const window = element.ownerDocument.defaultView;
  // @ts-ignore
  return window.getComputedStyle(element, null);
}

// @ts-ignore
export function getScrollParent(element: HTMLElement) {
  // Return body, `getScroll` will take care to get the correct `scrollTop` from it
  if (!element) {
    return document.body;
  }

  switch (element.nodeName) {
    case 'HTML':
    case 'BODY':
      return element.ownerDocument.body;
    case '#document':
      // @ts-ignore
      return element.body;
    default:
  }

  // Firefox want us to check `-x` and `-y` variations as well
  const { overflow, overflowX, overflowY } = getStyleComputedProperty(element);
  if (/(auto|scroll|overlay)/.test(overflow + overflowY + overflowX)) {
    return element;
  }

  return getScrollParent(getParentNode(element));
}

export const buildChildrenObject = <T>(
  children: React.ReactNode,
  childrenObject: T,
) => {
  return React.Children.toArray(children).reduce(
    (acc: T, child) => {
      if (!React.isValidElement(child)) {
        return acc;
      }

      if (!child.type || !(child.type as any).displayName) {
        return acc;
      }

      const name = (child.type as any).displayName.split('.').pop();
      // @ts-ignore
      acc[name] = child;
      return acc;
    },
    childrenObject || ({} as T),
  );
};

export interface ElementProps {
  children: any;
}
export const createComponentThatRendersItsChildren = (displayName: string) => {
  const Element: React.FunctionComponent<ElementProps> = ({ children }) =>
    typeof children === 'string'
      ? React.createElement('div', {}, children)
      : children;

  Element.displayName = displayName;

  return Element;
};

export const noop = () => null;

export const isReactElement = <T>(
  child: any,
  Element: React.ComponentType<T>,
): child is React.ReactElement<T> => {
  return child && child.type === Element;
};

export const isStatelessComponent = (Component: React.FC<any>) =>
  !(Component.prototype && Component.prototype.render);

export const attachClasses = (node: HTMLElement | null, classnames: string) =>
  node && node.classList.add(...classnames.split(' '));
export const detachClasses = (node: HTMLElement | null, classnames: string) =>
  node && node.classList.remove(...classnames.split(' '));

export const shouldAnimatePopover = ({
  timeout,
}: {
  timeout?: number | { enter: number; exit: number };
}) => {
  if (typeof timeout === 'object') {
    const { enter, exit } = timeout;

    return (
      typeof enter !== 'undefined' &&
      typeof exit !== 'undefined' &&
      (enter > 0 || exit > 0)
    );
  }

  return !!timeout;
};

export const getArrowShift = (shift: number | undefined, direction: string) => {
  if (!shift && !isTestEnv) {
    return {};
  }

  if (direction.startsWith('top') || direction.startsWith('bottom')) {
    return { left: `${shift}px` };
  }

  if (direction.startsWith('left') || direction.startsWith('right')) {
    return { top: `${shift}px` };
  }

  // Arrow can't be shifted when using automatic positioning
  return {};
};
